import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC, PropsWithChildren, useEffect, useState } from 'react'

import { VimeoPlayer } from '@Components/CMS/Video/VimeoPlayer'
import { useHasIntersected } from '@Globals/Hooks/Intersection'
import { useTranslation } from '@Globals/Translations'

import styles from './Video.module.scss'

export const Loading: FC<PropsWithChildren<{ className?: string }>>
    = ({ children, className }) => <div className={clsx(styles.loading, className)}>{children}</div>

export interface Settings {
  autoplay?: boolean
  loop?: boolean
  sound?: boolean
}

interface VideoPlayerProps {
  autoplay: boolean
  disableControls?: boolean
  hasHeightAuto: boolean
  loadVideo?: boolean
  settings?: Settings
  videoID: string
  videoType: 'vimeo' | 'youtube'
}

const VideoPlayer = ({
  autoplay = false,
  disableControls = false,
  hasHeightAuto,
  loadVideo,
  settings,
  videoID,
  videoType,
}: VideoPlayerProps) => (
  <div className="videoWrapInner">
    {videoType === 'youtube' && (
      <div className={clsx(hasHeightAuto && 'innerPadding')}>
        <iframe
          allowFullScreen
          frameBorder="0"
          src={`https://www.youtube.com/embed/${videoID}?autoplay=${autoplay ? 1 : 0}`}
          title={videoID}
        />
      </div>
    )}
    {videoType === 'vimeo' && (
      <VimeoPlayer
        className={clsx(hasHeightAuto && 'vimeoVideo')}
        enableControls={!disableControls}
        isVisible={loadVideo}
        options={{
          autoplay: settings?.autoplay,
          muted: !settings?.sound,
          loop: settings?.loop,
        }}
        videoID={videoID}
      />
    )}
  </div>
)

const ProductsCaption = ({ products }: { products: { link: string, title: string }[] }) => {
  const { t } = useTranslation()

  if (products?.length === 0) {
    return null
  }

  return (
    <p className={styles.productNames}>
      {t('cms:CMS_IMAGE_MODULE_SHOWING_LABEL')}
      {' '}
      {map(products, (item, index) => {
        let seperator = ''
        // Output: Product A, Product B, Product C & Product D
        if (index < products.length - 2) {
          seperator = ', '
        } else if (index < products.length - 1) {
          seperator = ' & '
        }

        return (
          <React.Fragment key={index}>
            <a href={item.link}>{item.title}</a>
            {seperator}
          </React.Fragment>
        )
      })}
    </p>
  )
}

export interface VideoProps {
  blockIndex: number
  caption?: string
  className?: string
  /** In case of video being cut: Used to decide what part of the video to show  */
  desktopCropping: 'top' | 'center' | 'bottom'
  /** Used to set a default height when loading - value is 'auto' or 10,20,...,100  */
  desktopMaxHeight: string
  disableControls?: boolean
  fillContainer?: boolean
  id?: string
  isInjection?: boolean
  products?: { link: string, title: string }[]
  settings?: Settings
  type: 'full-screen' | 'full' | 'column' | 'fill-container'
  videoId: string
  videoMobileId?: string
  videoType: 'vimeo' | 'youtube'
  withMargin?: boolean
}

export const Video: FC<VideoProps> = ({
  blockIndex,
  caption,
  className,
  desktopCropping,
  desktopMaxHeight = 'auto',
  disableControls,
  fillContainer = false,
  id,
  isInjection,
  products,
  settings,
  type,
  videoId,
  videoMobileId,
  videoType,
  withMargin = false,
}) => {
  const [loadVideo, setLoadVideo] = useState<boolean>(false)
  const [videoRef, hasIntersected] = useHasIntersected('200px', 0.01)

  useEffect(() => {
    if (hasIntersected || blockIndex < 3) {
      setLoadVideo(true)
    }
  }, [blockIndex, hasIntersected])

  const hasFullScreenCustomHeight = type === 'full-screen' && desktopMaxHeight !== 'auto'
  const useBothDesktopAndMobile = videoId && videoMobileId && videoId !== videoMobileId

  let heightObj: React.CSSProperties = {}

  if (hasFullScreenCustomHeight) {
    heightObj = {
      height: `${desktopMaxHeight}vw`,
      maxHeight: `${desktopMaxHeight}vw`,
    }
  } else if (fillContainer) {
    heightObj = {
      height: '100%',
      maxHeight: '100%',
    }
  }

  return (
    <div
      className={clsx(
        'videoWrapper',
        styles.videoWrapper,
        type === 'full-screen' && styles.fullScreen,
        type === 'full' && styles.fullWidth,
        type === 'column' && styles.column,
        type === 'fill-container' && styles.fillContainer,
        isInjection && styles.isInjection,
        withMargin && styles.withMargin,
        className,
      )}
      id={id}
      ref={videoRef}
    >
      {/* Desktop video */}
      <div
        className={clsx(
          styles.videoWrapOuter,
          useBothDesktopAndMobile && styles.desktop,
          (hasFullScreenCustomHeight || fillContainer) && styles[desktopCropping],
        )}
        data-video-id={videoId}
        style={heightObj}
      >
        {loadVideo && (
          <VideoPlayer
            autoplay={!!settings?.autoplay}
            disableControls={disableControls}
            hasHeightAuto={desktopMaxHeight === 'auto'}
            loadVideo={loadVideo}
            settings={{
              ...settings,
              ...(settings?.autoplay && { autoplay: settings.autoplay }),
            }}
            videoID={videoId}
            videoType={videoType}
          />
        )}
      </div>

      {/* Has mobile video */}
      {useBothDesktopAndMobile && (
        <div className={clsx(styles.videoWrapOuter, styles.mobile)} data-video-id={videoMobileId}>
          {loadVideo && (
            <VideoPlayer
              autoplay={!!settings?.autoplay}
              disableControls={disableControls}
              hasHeightAuto={desktopMaxHeight === 'auto'}
              loadVideo={loadVideo}
              settings={{
                ...settings,
                ...(settings?.autoplay && { autoplay: !!settings?.autoplay }),
              }}
              videoID={videoMobileId}
              videoType={videoType}
            />
          )}
        </div>
      )}

      {/* Video product && caption */}
      {(caption || (products?.length || 0) > 0) && (
        <div className={clsx(styles.videoInfo, caption && styles.withCaption)}>
          <ProductsCaption products={products || []} />
          {caption && <p className={styles.videoCaption}>{caption}</p>}
        </div>
      )}
    </div>
  )
}
